import type {
  AcademyAdditionalDataRequest,
  AcademyAdditionalDataUpdateRequest,
  AcademyInitializationRequest,
  AcademyInitializationUpdateRequest,
  AcademyMetadataUpdateRequest,
  AcademyRequestFreePremiumRequest,
  AcademySubscriptionOfferRequest,
  AcademyUpdateRequest,
  AcademyWorksheetShareDetailSetting,
  AcademyWorksheetShareModificationRequest,
  B2GContractSignatureRequest,
  CommonResponseAcademyResponse,
  DownloadB2GContractPdfData,
  GetB2GContractData,
  GetEncodedNiceInfoData,
  GetEncodedNiceInfoParams,
  InitializeAcademyData,
  ProcessByTypeData,
  ProcessByTypeParams,
  RequestFreePremiumData,
  SendAcademySubscriptionOfferData,
  SignB2GContractData,
  UpdateAcademyAdditionalData1Data,
  UpdateAcademyAdditionalDataData,
  UpdateAcademyData,
  UpdateAcademyLogoData,
  UpdateAcademyMetadata1Data,
  UpdateAcademyMetadataData,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'
import type { WithNullableOptionals } from '~/http/request'

import {
  adaptAcademy,
  adaptAcademyDetail,
  adaptAcademyWorksheetShareSetting,
  adaptB2gContract,
  adaptB2gContractPdf,
  adaptNiceEncodeData,
  adaptNiceIdentity,
} from './adapters'
import type {
  Academy,
  AcademyDetail,
  AcademyWorksheetShareSetting,
  B2gContract,
  B2gContractPdf,
  NiceAuthType,
  NiceEncodeData,
  NiceIdentity,
} from './schemas'

export type { AcademySubscriptionOfferRequest }

/** 문자 발신번호 본인 인증(NICE) 창을 여는 암호문을 받을 때 넘기는, 인증 성공·실패 뒤 돌아올 주소 */
export type NiceEncodeInfoParams = GetEncodedNiceInfoParams

/** 로그인한 사용자가 속한 학원 */
export const getAcademy = async (meta?: RequestMeta): Promise<Academy> => {
  const body = await httpClient().get<CommonResponseAcademyResponse>('/academies', { meta })
  return adaptAcademy(body.data)
}

export type AcademyWorksheetShareBody = AcademyWorksheetShareModificationRequest

/** 다른 기관 학습지 공유 설정을 바꾸고 바뀐 설정을 받는다. 응답은 CommonResponse 봉투 없이 본문 그대로다 */
export const updateAcademyWorksheetShareSetting = async (
  body: AcademyWorksheetShareBody,
  meta?: RequestMeta,
): Promise<AcademyWorksheetShareSetting> => {
  const setting = await httpClient().patch<AcademyWorksheetShareDetailSetting>(
    '/worksheet/shared/v2',
    body,
    { meta },
  )
  return adaptAcademyWorksheetShareSetting(setting)
}

/***************** 학원 상세와 설정 *****************/
// 아래 요청은 응답 본문을 쓰지 않는다. 요청 본문의 optional 필드는 서버가 nullable 로 받는다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 academy/controller/AcademyController.kt·request/ 로 확인.

/** 로그인한 사용자가 속한 학원의 상세. 선생님 앱이 쓴다 */
export const getAcademyDetail = async (meta?: RequestMeta): Promise<AcademyDetail> => {
  const body = await httpClient().get<CommonResponseAcademyResponse>('/academies', { meta })
  return adaptAcademyDetail(body.data)
}

export type AcademyUpdateBody = WithNullableOptionals<AcademyUpdateRequest>

/** 학원 이름·이메일·고객 분류·주소를 바꾼다. 보내지 않은(null) 필드는 그대로 둔다 */
export const updateAcademy = async (body: AcademyUpdateBody, meta?: RequestMeta): Promise<void> => {
  await httpClient().patch<UpdateAcademyData>('/academies', body, { meta })
}

/** operation 과, REPLACE 면 logoFile 을 담은 multipart 폼이다 */
export const updateAcademyLogo = async (form: FormData, meta?: RequestMeta): Promise<void> => {
  await httpClient().patch<UpdateAcademyLogoData>('/academies/logo', form, { meta })
}

// curriculumList·subjectList 는 서버가 curriculums·subjects 로 옮기며 남긴 옛 이름이라 보내지 않는다
export type AcademyInitializationBody = WithNullableOptionals<
  Omit<AcademyInitializationRequest, 'curriculumList' | 'subjectList'>
>

/** 가입 직후 학원 규모·학교급·과목을 처음 저장한다 */
export const initializeAcademy = async (
  body: AcademyInitializationBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<InitializeAcademyData>('/academies/metadata', body, { meta })
}

export type AcademyInitializationUpdateBody =
  WithNullableOptionals<AcademyInitializationUpdateRequest>

/** 학원명·주소와 함께 학원 규모·학교급·과목을 고친다 */
export const updateAcademyInitialization = async (
  body: AcademyInitializationUpdateBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().put<UpdateAcademyMetadataData>('/academies/metadata', body, { meta })
}

/** 메타데이터 키 하나. 값은 서버가 문자열로 저장하고 읽을 때 키의 타입으로 푼다(불리언이면 'true'·'false') */
export type AcademyMetadataItemBody = AcademyMetadataUpdateRequest

export const updateAcademyMetadataItem = async (
  body: AcademyMetadataItemBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<UpdateAcademyMetadata1Data>('/academies/metadata', body, { meta })
}

export type SampleSignatureWorkbookShowBody = AcademyAdditionalDataUpdateRequest

/** 견본 시그니처 교재를 목록에 보일지 정한다 */
export const updateSampleSignatureWorkbookShow = async (
  body: SampleSignatureWorkbookShowBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().put<UpdateAcademyAdditionalDataData>('/academies/metadata/v2', body, { meta })
}

export type AcademyAdditionalDataBody = WithNullableOptionals<AcademyAdditionalDataRequest>

/** 견본 시그니처 교재 노출과 학교급·학년별 시험 자동 배정을 바꾼다. 보내지 않은 필드는 그대로 둔다 */
export const updateAcademyAdditionalData = async (
  body: AcademyAdditionalDataBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().put<UpdateAcademyAdditionalData1Data>('/academies/additional-data', body, {
    meta,
  })
}

export type FreePremiumBody = AcademyRequestFreePremiumRequest

/** 프리미엄 무료 체험을 신청한다. 시작일은 yyyy-MM-dd 다 */
export const requestFreePremium = async (
  body: FreePremiumBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<RequestFreePremiumData>('/academies/free-premium', body, { meta })
}

/***************** B2G 계약 *****************/
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 academy/controller/B2gContractController.kt·
// request/B2gContractSignatureRequest.kt 로 확인.

/** 로그인한 학원의 B2G 계약. B2G 학원이 아니거나 계약이 없으면 null 이다 */
export const getB2gContract = async (meta?: RequestMeta): Promise<B2gContract | null> => {
  const body = await httpClient().get<GetB2GContractData>('/b2g/contract', { meta })
  return adaptB2gContract(body.data)
}

/** 두 서명은 서명 이미지의 Base64 문자열이다 */
export type B2gContractSignatureBody = B2GContractSignatureRequest

export const signB2gContract = async (
  body: B2gContractSignatureBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<SignB2GContractData>('/b2g/contract', body, { meta })
}

/** 서명한 계약서 PDF 주소. 계약이 없으면 서버가 오류로 응답한다 */
export const getB2gContractPdf = async (meta?: RequestMeta): Promise<B2gContractPdf> => {
  const body = await httpClient().get<DownloadB2GContractPdfData>('/b2g/contract/pdf', { meta })
  return adaptB2gContractPdf(body.data)
}

/***************** NICE 본인인증 *****************/
// encodeData 는 NICE 인증 창이 성공 주소로 돌려준 암호문이다. 서버는 @RequestParam 으로 받아 폼 필드로도 읽으므로
// 길이가 긴 암호문을 쿼리에 싣지 않고 multipart 폼으로 보낸다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 academy/controller/AcademyController.kt(processByType) 로 확인.
const completeNiceAuth = async (
  type: NiceAuthType,
  encodeData: ProcessByTypeParams['encodeData'],
  meta?: RequestMeta,
) => {
  const form = new FormData()
  form.append('encodeData', encodeData)
  return httpClient().patch<ProcessByTypeData>(`/academies/nice-auth-success/${type}`, form, {
    meta,
  })
}

/** 본인인증한 휴대폰 번호를 학원의 문자 발신번호로 등록하고 문자 서비스를 켠다 */
export const registerSmsPhoneNumberByNice = async (
  encodeData: ProcessByTypeParams['encodeData'],
  meta?: RequestMeta,
): Promise<void> => {
  await completeNiceAuth('UPDATE_PHONE_NUMBER', encodeData, meta)
}

/** 본인인증 창을 여는 암호문을 받는다. 인증 뒤 NICE 가 성공·실패 주소로 돌려보낸다 */
export const getNiceEncodeData = async (
  params: NiceEncodeInfoParams,
  meta?: RequestMeta,
): Promise<NiceEncodeData> => {
  const body = await httpClient().get<GetEncodedNiceInfoData>('/academies/encoded-nice-info', {
    params,
    meta,
  })
  return adaptNiceEncodeData(body.data)
}

/** 정산 정보 등록 전 본인인증한 사람의 정보를 받는다 */
export const getNiceIdentityForSettlement = async (
  encodeData: ProcessByTypeParams['encodeData'],
  meta?: RequestMeta,
): Promise<NiceIdentity> => {
  const body = await completeNiceAuth('SETTLEMENT_INFO_REGISTER', encodeData, meta)
  return adaptNiceIdentity(body.data)
}

/**
 * 무료 체험이 끝나 이용이 막힌 학원이 구독 상담을 신청한다. 로그인이 막힌 상태라 로그인 거부 응답이 준
 * 일회용 토큰을 `x-subscription-offer-token` 헤더로 싣는다.
 */
export const requestSubscriptionOffer = async (
  body: AcademySubscriptionOfferRequest,
  token: string,
  academyId: Academy['academyId'],
): Promise<void> => {
  await httpClient().post<SendAcademySubscriptionOfferData>(
    `/academies/${academyId}/subscription-offer`,
    body,
    { headers: { 'x-subscription-offer-token': token } },
  )
}
